import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDocumentPages } from '../../../hooks/useDocumentPages';
import { BlockOverlay } from './BlockOverlay';
/**
 * Page Canvas — renders the PDF page image with block overlays.
 *
 * Zoom is applied via CSS `transform: scale()` on the page+overlay
 * container so the image and bounding boxes scale together perfectly.
 * No coordinate recalculation needed — the overlay positions as
 * percentages of the natural image size and the scale multiplies both
 * uniformly.
 */ export const PageCanvas = ({ projectId, documentId, pageNumber, viewport, selectedBlockId, highlightedBlockIds, highlightBbox, onBlockSelect, onPanChange, pageLoader })=>{
    const containerRef = useRef(null);
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState({
        x: 0,
        y: 0
    });
    const [imageDimensions, setImageDimensions] = useState({
        width: 0,
        height: 0
    });
    // ONE hook call, always. The choice is made from a prop the viewer fixes at
    // mount, so the hook order is stable for the component's whole life.
    const loadPage = pageLoader ?? useDocumentPages;
    const { data: pageData, isLoading, error } = loadPage(projectId, documentId, pageNumber);
    useEffect(()=>{
        if (pageData?.imageUrl) {
            const img = new Image();
            img.onload = ()=>{
                setImageDimensions({
                    width: img.naturalWidth,
                    height: img.naturalHeight
                });
            };
            img.src = pageData.imageUrl;
        }
    }, [
        pageData?.imageUrl
    ]);
    const handleMouseDown = useCallback((e)=>{
        if (e.button === 0) {
            setIsPanning(true);
            setPanStart({
                x: e.clientX,
                y: e.clientY
            });
            e.preventDefault();
        }
    }, []);
    const handleMouseMove = useCallback((e)=>{
        if (isPanning) {
            const deltaX = e.clientX - panStart.x;
            const deltaY = e.clientY - panStart.y;
            onPanChange(deltaX, deltaY);
            setPanStart({
                x: e.clientX,
                y: e.clientY
            });
        }
    }, [
        isPanning,
        panStart,
        onPanChange
    ]);
    const handleMouseUp = useCallback(()=>setIsPanning(false), []);
    const handleMouseLeave = useCallback(()=>setIsPanning(false), []);
    const handleWheel = useCallback((_e)=>{
    // Zoom is controlled by the parent via viewport.zoom.
    // Ctrl+Wheel zoom can be wired here in a future pass.
    }, []);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full items-center justify-center bg-slate-100",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "h-8 w-8 animate-spin rounded-full border-[3px] border-slate-200 border-t-blue-500"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-sm text-slate-500",
                        children: "Loading page..."
                    })
                ]
            })
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full items-center justify-center bg-slate-100",
            children: /*#__PURE__*/ _jsx("span", {
                className: "text-sm text-red-600",
                children: "Failed to load page"
            })
        });
    }
    if (!pageData) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full items-center justify-center bg-slate-100",
            children: /*#__PURE__*/ _jsx("span", {
                className: "text-sm text-slate-500",
                children: "No page data available"
            })
        });
    }
    // Non-PDF documents (DOCX/XLSX/…) have blocks but no rasterized page image —
    // zou only renders PDFs. Offer the file itself instead of a blank canvas.
    if (!pageData.imageUrl) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex h-full flex-col items-center justify-center gap-2 bg-slate-100 p-6 text-center",
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: "text-sm font-medium text-slate-600",
                    children: "No page preview for this file type"
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "max-w-sm text-xs text-slate-500",
                    children: "Only PDFs render as pages here — download or open the original file to view its contents."
                }),
                /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    className: "mt-1 rounded border bg-white px-3 py-1.5 text-sm font-medium text-primary shadow-sm hover:bg-slate-50",
                    onClick: ()=>{
                        void import('../../../lib/connect/clients').then(async ({ artifactClient })=>{
                            try {
                                const dl = await artifactClient.generateDownloadUrl({
                                    artifactId: documentId
                                });
                                if (dl.url) window.open(dl.url, '_blank', 'noopener');
                            } catch  {
                            // Not a tesoro artifact (e.g. a pipeline doc id) — nothing to
                            // offer beyond the message above.
                            }
                        });
                    },
                    children: "Open original file"
                })
            ]
        });
    }
    // Use the image's natural dimensions as the base. The overlay positions
    // as percentages of these dimensions. CSS scale() zooms both together.
    const baseWidth = imageDimensions.width || pageData.dimensions.width;
    const baseHeight = imageDimensions.height || pageData.dimensions.height;
    // Fit-to-width baseline: pages render at ~150 DPI (1200-1700px wide), far
    // wider than any pane. Normalize so viewport.zoom === 1 means "page fills
    // the pane width" — zoom levels then scale relative to a fitted page
    // instead of raw pixels overflowing every container.
    const containerWidth = containerRef.current?.clientWidth ?? 0;
    const fitScale = containerWidth > 0 && baseWidth > 0 ? Math.min(1, (containerWidth - 24) / baseWidth) : 1;
    return /*#__PURE__*/ _jsx("div", {
        ref: containerRef,
        className: "flex h-full w-full items-center justify-center overflow-auto bg-slate-100",
        onMouseDown: handleMouseDown,
        onMouseMove: handleMouseMove,
        onMouseUp: handleMouseUp,
        onMouseLeave: handleMouseLeave,
        onWheel: handleWheel,
        style: {
            cursor: isPanning ? 'grabbing' : 'grab'
        },
        children: /*#__PURE__*/ _jsx("div", {
            style: {
                transform: `translate(${viewport.panX}px, ${viewport.panY}px)`,
                transformOrigin: 'center center'
            },
            children: /*#__PURE__*/ _jsxs("div", {
                className: "relative bg-white shadow-xl ring-1 ring-slate-200/80",
                style: {
                    width: `${baseWidth}px`,
                    height: `${baseHeight}px`,
                    transform: `scale(${viewport.zoom * fitScale})`,
                    transformOrigin: 'center center'
                },
                children: [
                    /*#__PURE__*/ _jsx("img", {
                        src: pageData.imageUrl,
                        alt: `Page ${pageNumber}`,
                        className: "h-full w-full",
                        draggable: false
                    }),
                    /*#__PURE__*/ _jsx(BlockOverlay, {
                        blocks: pageData.blocks,
                        pageDimensions: pageData.dimensions,
                        selectedBlockId: selectedBlockId,
                        highlightedBlockIds: highlightedBlockIds,
                        highlightBbox: highlightBbox,
                        onBlockSelect: onBlockSelect
                    })
                ]
            })
        })
    });
};
